<template>
	<PrimeDialog
		:closable="true"
		:dismissable-mask="true"
		:modal="true"
		:style="{ width: '640px', maxWidth: '92vw' }"
		:visible="visible"
		@update:visible="onUpdateVisible"
	>
		<template #header>
			<div class="cheatsheetHeader">
				<i class="fa-regular fa-keyboard cheatsheetHeader__icon" />
				<div>
					<div class="cheatsheetHeader__title">{{ $t('shortcuts_cheatsheet_title') }}</div>
					<div class="cheatsheetHeader__caption">{{ $t('shortcuts_cheatsheet_caption') }}</div>
				</div>
			</div>
		</template>

		<div class="cheatsheet">
			<IconField class="cheatsheet__search">
				<InputIcon><i class="fa-regular fa-magnifying-glass" /></InputIcon>
				<InputText
					:placeholder="$t('shortcuts_cheatsheet_search_placeholder')"
					fluid
					v-model="searchTerm"
				/>
			</IconField>

			<div
				class="cheatsheetEmpty"
				v-if="groupedBindings.length === 0"
			>
				{{ $t('shortcuts_cheatsheet_empty') }}
			</div>

			<div
				:key="group.key"
				class="cheatsheetGroup"
				v-for="group in groupedBindings"
			>
				<div class="cheatsheetGroup__title">
					<i :class="group.icon" />
					<span>{{ $t(group.label) }}</span>
				</div>

				<div class="cheatsheetGroup__body">
					<div
						:key="entry.action.id"
						class="cheatsheetRow"
						v-for="entry in group.entries"
					>
						<span class="cheatsheetRow__icon">
							<i :class="`${menuItemsSolid ? 'fa-solid' : 'fa-regular'} ${entry.action.icon}`" />
						</span>
						<span class="cheatsheetRow__label">{{ $t(entry.action.labelI18nKey) }}</span>
						<span class="cheatsheetRow__keys">{{ entry.keysDisplay }}</span>
					</div>
				</div>
			</div>
		</div>

		<template #footer>
			<PrimeButton
				:label="$t('shortcuts_cheatsheet_open_settings')"
				@click="onOpenSettings"
				icon="fa-regular fa-sliders"
				severity="secondary"
				text
			/>
		</template>
	</PrimeDialog>
</template>

<script lang="ts">
import type { ShortcutCategoryKey } from 'src/stores/shortcuts/types';

import { computed, defineComponent, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import PrimeButton from 'primevue/button';
import PrimeDialog from 'primevue/dialog';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputText from 'primevue/inputtext';
import { activeShortcutContexts } from 'src/composables/useShortcutRegistry';
import { useShortcutsStore } from 'src/stores/shortcuts';
import { useUserInterfaceSettingsStore } from 'src/stores/userInterfaceSettings';

import { EventBus } from 'src/util/eventBus/EventBus';
import { SHORTCUT_ACTIONS_BY_ID } from 'src/util/shortcuts/actionsRegistry';
import { formatKeysForDisplay } from 'src/util/shortcuts/keyNormalizer';
import { SHORTCUT_EVENT_CHEATSHEET_TOGGLE } from 'src/util/shortcuts/shortcutEvents';

interface CheatsheetEntry {
	action: (typeof SHORTCUT_ACTIONS_BY_ID)[string];
	keysDisplay: string;
}

interface CheatsheetGroup {
	entries: CheatsheetEntry[];
	icon: string;
	key: ShortcutCategoryKey;
	label: string;
}

export default defineComponent({
	components: {
		IconField,
		InputIcon,
		InputText,
		PrimeButton,
		PrimeDialog,
	},
	name: 'ShortcutCheatsheetOverlay',
	setup() {
		const { t } = useI18n();
		const store = useShortcutsStore();
		const userInterfaceSettingsStore = useUserInterfaceSettingsStore();
		const router = useRouter();
		const eventBus = inject<EventBus>('eventBus');

		const visible = ref(false);
		const searchTerm = ref('');

		const menuItemsSolid = computed((): boolean => userInterfaceSettingsStore.menuItemsSolid);

		function toggle(): void {
			visible.value = !visible.value;
		}

		const categoryMeta: Record<ShortcutCategoryKey, { icon: string; label: string }> = {
			detail: { icon: 'fa-regular fa-file-lines', label: 'shortcuts_scope_detail' },
			global: { icon: 'fa-regular fa-globe', label: 'shortcuts_scope_global' },
			list: { icon: 'fa-regular fa-list', label: 'shortcuts_scope_list' },
			navigation: { icon: 'fa-regular fa-compass', label: 'shortcuts_scope_navigation' },
		};

		const activeContextSet = computed((): Set<string> => {
			return new Set(activeShortcutContexts.value);
		});

		const normalizedSearchTerm = computed((): string => searchTerm.value.trim().toLowerCase());

		const groupedBindings = computed((): CheatsheetGroup[] => {
			if (!store.enabled) {
				return [];
			}

			const groupsMap = new Map<ShortcutCategoryKey, CheatsheetGroup>();
			const search = normalizedSearchTerm.value;

			for (const binding of Object.values(store.bindings)) {
				if (!binding.enabled || binding.keys.length === 0) {
					continue;
				}

				const action = SHORTCUT_ACTIONS_BY_ID[binding.id];

				if (action === undefined) {
					continue;
				}

				const matchesActiveContext = action.contexts.some((context) => activeContextSet.value.has(context));

				if (!matchesActiveContext) {
					continue;
				}

				if (search.length > 0) {
					const label = t(action.labelI18nKey).toLowerCase();
					const keys = binding.keys.toLowerCase();

					if (!label.includes(search) && !keys.includes(search)) {
						continue;
					}
				}

				const group = groupsMap.get(action.category) ?? {
					entries: [],
					icon: categoryMeta[action.category].icon,
					key: action.category,
					label: categoryMeta[action.category].label,
				};

				group.entries.push({
					action,
					keysDisplay: formatKeysForDisplay(binding.keys),
				});

				groupsMap.set(action.category, group);
			}

			const orderedKeys: ShortcutCategoryKey[] = ['global', 'navigation', 'list', 'detail'];

			return orderedKeys
				.map((key) => groupsMap.get(key))
				.filter((group): group is CheatsheetGroup => group !== undefined)
				.map((group) => ({
					...group,
					entries: [...group.entries].sort((left, right) =>
						left.action.labelI18nKey.localeCompare(right.action.labelI18nKey)
					),
				}));
		});

		function onOpenSettings(): void {
			visible.value = false;
			void router
				.push({
					name: 'settings',
					params: { selectedSettingsTab: 'shortcuts' },
				})
				.catch(() => {
					/* ignore navigation aborts */
				});
		}

		function onUpdateVisible(nextVisible: boolean): void {
			visible.value = nextVisible;
		}

		onMounted(() => {
			eventBus?.on(SHORTCUT_EVENT_CHEATSHEET_TOGGLE, toggle);
		});

		onBeforeUnmount(() => {
			eventBus?.off(SHORTCUT_EVENT_CHEATSHEET_TOGGLE, toggle);
		});

		watch(visible, (next) => {
			if (!next) {
				searchTerm.value = '';
			}
		});

		return {
			groupedBindings,
			menuItemsSolid,
			onOpenSettings,
			onUpdateVisible,
			searchTerm,
			visible,
		};
	},
});
</script>

<style lang="scss" scoped>
.cheatsheetHeader {
	align-items: center;
	display: flex;
	gap: 12px;
}

.cheatsheetHeader__icon {
	color: var(--app-primary, #004994);
	font-size: var(--text-lg);
}

.cheatsheetHeader__title {
	color: var(--settings-text, #22304d);
	font-size: var(--text-lg);
	font-weight: 700;
}

.cheatsheetHeader__caption {
	color: var(--settings-muted, rgba(20, 34, 59, 0.7));
	font-size: var(--text-sm);
}

.cheatsheet {
	display: flex;
	flex-direction: column;
	gap: 16px;
	max-height: 65vh;
	overflow-y: auto;
	padding: 4px 2px;
}

.cheatsheet__search {
	background: var(--p-surface-0, #ffffff);
	margin: -4px -2px 0;
	padding: 4px 2px 8px;
	position: sticky;
	top: -4px;
	z-index: 1;
}

.body--dark .cheatsheet__search {
	background: var(--p-surface-800, #1e293b);
}

.cheatsheetEmpty {
	color: var(--settings-muted, rgba(20, 34, 59, 0.6));
	padding: 24px;
	text-align: center;
}

.cheatsheetGroup__title {
	align-items: center;
	color: var(--settings-muted, rgba(20, 34, 59, 0.7));
	display: flex;
	font-size: var(--text-sm);
	font-weight: 700;
	gap: 8px;
	letter-spacing: 0.06em;
	margin-bottom: 8px;
	text-transform: uppercase;

	i {
		color: var(--app-primary, #004994);
	}
}

.cheatsheetGroup__body {
	display: grid;
	gap: 4px;
}

.cheatsheetRow {
	align-items: center;
	display: grid;
	gap: 12px;
	grid-template-columns: 20px minmax(0, 1fr) auto;
	padding: 6px 4px;
}

.cheatsheetRow__icon {
	color: var(--app-primary, #004994);
	display: inline-flex;
	font-size: inherit;
	justify-content: center;
	opacity: 0.7;
}

.cheatsheetRow__label {
	color: var(--settings-text, #22304d);
	font-size: var(--text-sm);
	min-width: 0;
}

.cheatsheetRow__keys {
	background: var(--p-surface-100, #f1f5f9);
	border: 1px solid var(--settings-border-strong, rgba(25, 42, 70, 0.14));
	border-radius: var(--radius-md, 8px);
	color: var(--settings-text, #22304d);
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
	font-size: var(--text-sm);
	font-weight: 600;
	padding: 4px 10px;
	white-space: nowrap;
}

.body--dark .cheatsheetRow__keys {
	background: var(--p-surface-800, #1e293b);
	border-color: var(--p-surface-700, rgba(255, 255, 255, 0.1));
	color: rgba(255, 255, 255, 0.92);
}

.body--dark .cheatsheetHeader__title,
.body--dark .cheatsheetRow__label {
	color: rgba(255, 255, 255, 0.92);
}

.body--dark .cheatsheetHeader__caption,
.body--dark .cheatsheetGroup__title,
.body--dark .cheatsheetEmpty {
	color: rgba(255, 255, 255, 0.65);
}
</style>
